import * as React from 'react';
import { Avatar } from '../../primitives/Avatar/Avatar';
import { TrustBadge } from '../TrustBadge/TrustBadge';
import type { Member } from '../_types';
import {
  root as rootCls,
  chipDivider as chipDividerCls,
  textColumn as textColumnCls,
  name as nameCls,
  role as roleCls,
  onlineDot as onlineDotCls,
  avatarSlot as avatarSlotCls,
  badgeSlot as badgeSlotCls,
} from './MemberChip.css';

export type MemberChipSize = 'sm' | 'md' | 'lg';
export type MemberChipVariant = 'inline' | 'formal';

export interface MemberChipProps
  // `onSelect` is overridden to the semantic member callback (the native
  // `onSelect` is the rarely-relevant text-selection event on a div).
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'onSelect'> {
  member: Member;
  size?: MemberChipSize;
  variant?: MemberChipVariant;
  /** Force showing the role line. Default: true for md/lg, false for sm. */
  showRole?: boolean;
  /** Render the agent's trust level as an inline TrustBadge (md/lg only). */
  trustLevelOnAgents?: boolean;
  /** Show the online status dot to the left of the avatar. */
  showOnlineDot?: boolean;
  /**
   * Semantic "this member was chosen" callback — receives the `Member`
   * directly (callers almost always want the data, not the DOM event).
   * Supplying it makes the chip interactive: `role="button"`, focusable,
   * Enter / Space activation, and a synthesised `aria-label`.
   *
   * Native `onClick` is left untouched and still fires the raw
   * `MouseEvent` if you also pass it; use `onSelect` for the semantic
   * action and `onClick` only when you genuinely need the DOM event.
   */
  onSelect?: (member: Member) => void;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLDivElement>;
}

const AVATAR_SIZE: Record<MemberChipSize, 'xs' | 'sm' | 'md' | 'lg' | 'xl'> = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
};

/**
 * MemberChip — avatar + name + role chip for human / agent members.
 * Three sizes; two visual variants (`inline` and `formal` — formal adds
 * a vertical divider between the avatar and the text column).
 *
 * Becomes interactive when `onSelect` is supplied — the chip becomes a
 * `role="button"` with Enter / Space activation, and `onSelect` receives
 * the `Member` directly (callers almost always want the data, not the
 * DOM event). Native `onClick` is left to behave natively; pass it only
 * when you genuinely need the raw `MouseEvent`.
 *
 * `showOnlineDot` adds a small status dot to the left of the avatar;
 * `trustLevelOnAgents` (md/lg only) adds an inline `<TrustBadge>` after
 * the text column for agent members.
 */
export function MemberChip({
  member,
  size = 'md',
  variant = 'inline',
  showRole,
  trustLevelOnAgents = false,
  showOnlineDot = false,
  onSelect,
  // Native handlers are pulled out so the semantic `onSelect` can compose
  // with them rather than being clobbered by the trailing `{...rest}`.
  onClick,
  onKeyDown,
  'aria-label': ariaLabel,
  ref,
  className,
  ...rest
}: MemberChipProps) {
  const roleVisible = showRole ?? size !== 'sm';
  const interactive = Boolean(onSelect);
  const isAgent = member.type === 'agent';

  const cls = [rootCls({ interactive, variant }), className].filter(Boolean).join(' ');

  return (
    <div
      ref={ref}
      className={cls}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      // Only synthesise the rich `aria-label` on the interactive (button)
      // chip — a non-interactive chip's visible text IS its accessible
      // name, and overriding it with an aria-label adds AT noise. A
      // consumer-supplied `ariaLabel` is still respected in both modes.
      aria-label={
        interactive
          ? (ariaLabel ??
            `${member.name}${member.role ? `, ${member.role}` : ''}${
              isAgent && member.trustLevel ? `, trust ${member.trustLevel}` : ''
            }`)
          : ariaLabel
      }
      // Native `onClick` fires unchanged; `onSelect` adds the semantic
      // member callback on top of it.
      onClick={
        onClick || onSelect
          ? (e) => {
              onClick?.(e);
              onSelect?.(member);
            }
          : undefined
      }
      onKeyDown={
        onKeyDown || interactive
          ? (e) => {
              onKeyDown?.(e);
              if (interactive && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onSelect?.(member);
              }
            }
          : undefined
      }
      {...rest}
    >
      {showOnlineDot && (
        <span className={onlineDotCls({ online: Boolean(member.online) })} aria-hidden="true" />
      )}
      <span className={avatarSlotCls}>
        <Avatar
          name={member.name}
          size={AVATAR_SIZE[size]}
          ringTone={isAgent ? 'purple' : 'none'}
        />
      </span>
      {variant === 'formal' && <span className={chipDividerCls} aria-hidden="true" />}
      <span className={textColumnCls}>
        <span className={nameCls({ size })}>{member.name}</span>
        {roleVisible && member.role && <span className={roleCls}>{member.role}</span>}
      </span>
      {trustLevelOnAgents && isAgent && member.trustLevel && size !== 'sm' && (
        <span className={badgeSlotCls}>
          <TrustBadge level={member.trustLevel} size="sm" />
        </span>
      )}
    </div>
  );
}
MemberChip.displayName = 'MemberChip';
